import {Maximize2, Pen, Ruler, Trash2} from "lucide-react";
import {Dungeon} from "@/dungeons/model/Dungeon.ts";
import {Button} from "@/components/ui/button.tsx";
import DeleteConfirm from "@/components/forms/DeleteConfirm.tsx";
import AuditMetadata from "@/components/management/AuditMetadata.tsx";
import {srcUrl} from "@/api/ImageApi.ts";
import {dungeonCardImage} from "@/dungeons/dungeonCardImage.ts";

export default function DungeonDetail({dungeon, onEdit, onOpenEditor, onDelete}: {
    dungeon: Dungeon;
    onEdit: () => void;
    onOpenEditor: () => void;
    onDelete: () => Promise<void>;
}) {
    const sortedLevels = [...dungeon.levels].sort((a, b) => a.sortIndex - b.sortIndex);
    const cover = dungeonCardImage(dungeon);
    return <article className="space-y-5">
        <title>{`${dungeon.name} | DireDawn`}</title>
        <header className="overflow-hidden rounded-xl border border-border/80 bg-card/90 shadow-lg shadow-black/10 sm:flex">
            <div className="relative h-40 shrink-0 overflow-hidden bg-surface sm:w-64">
                {cover && <img src={srcUrl(cover)} alt="" className="size-full object-cover"/>}
                <div className="pointer-events-none absolute inset-0 bg-linear-to-t from-card/50 to-transparent"/>
            </div>
            <div className="min-w-0 flex-1 space-y-4 p-5 sm:p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <p className="mb-2 text-xs font-semibold uppercase tracking-[0.22em] text-primary">Dungeon verwalten</p>
                        <h1 className="text-3xl font-semibold tracking-tight">{dungeon.name}</h1>
                        <p className="mt-1 text-sm text-muted-foreground">{dungeon.levels.length} Level</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <Button variant="outline" onClick={onEdit}><Pen/>Bearbeiten</Button>
                        <Button variant="outline" onClick={onOpenEditor}><Ruler/>Editor</Button>
                        <DeleteConfirm title={`${dungeon.name} global löschen?`}
                                       description="Der Dungeon wird aus dem globalen Katalog gelöscht. Diese Aktion kann nicht rückgängig gemacht werden."
                                       onConfirm={onDelete}>
                            <Button variant="destructive" title="Löschen"
                                    onSelect={event => event.preventDefault()}><Trash2/>Löschen</Button>
                        </DeleteConfirm>
                    </div>
                </div>
                <AuditMetadata createdAt={dungeon.createdAt} updatedAt={dungeon.updatedAt}/>
            </div>
        </header>
        <section className="space-y-3" aria-label="Dungeon-Level">
            <h2 className="text-lg font-semibold">Level-Karten</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {sortedLevels.map(level => <article key={level.id}
                    className="overflow-hidden rounded-xl border border-border/80 bg-card/90 shadow-sm">
                    <div className="relative h-40 bg-black/20">
                        <img src={srcUrl(level.map.image)} alt={`Karte: ${level.name}`} className="size-full object-contain"/>
                        <Button className="absolute right-2 top-2" variant="outline" size="icon-sm" title="Originalgröße" asChild>
                            <a href={srcUrl(level.map.image)} target="_blank" rel="noreferrer"><Maximize2/></a>
                        </Button>
                    </div>
                    <div className="p-3 font-medium">{level.name}</div>
                </article>)}
            </div>
        </section>
    </article>;
}
